/**
 * Regras puras do Relatório de ambientes em aberto (Legado).
 *
 * "Em aberto" = ambiente da planilha GERENCIAL ainda sem CARREGAMENTO — o
 * mesmo recorte do relatório impresso "Ambientes aguardando carregamento"
 * que a equipe da cliente-piloto entregava ao dono (122 ambientes / 29 pedidos em
 * 02/10/2026, obra futura fora).
 *
 * Uma régua por número, e o rótulo diz qual (ADR-0059):
 * - STATUS do ambiente = `getAmbienteCurrentStatus` — a mesma da ficha do
 *   cliente e do painel lateral. Não existe um segundo cálculo de etapa aqui.
 * - ATRASO = prazo da etapa atual vencido, em dias CORRIDOS. Quando a etapa
 *   não tem limite próprio na planilha, o prazo é o da entrega
 *   (`prazoOrigem: "entrega"`), e a tela escreve "entrega" em vez de "limite".
 * - BLOQUEIO é selo, não situação: o ambiente bloqueado continua contando
 *   como atrasado se o prazo passou (decisão do dono, 02/10/2026).
 * - Terceiros e Pintura correm em paralelo: viram selo, com as mesmas regras
 *   das páginas Terceiros e Pintura & Lacagem.
 * - "Carregamento previsto" = coluna DATA REPROGRAMADA (é a que o relatório
 *   impresso usava); "Contrato" = ENTREGA PREVISTA.
 *
 * Sem React — testável em isolamento.
 */
import { chaveAmbiente } from "../MapaProducao/dados/chaves";
import { isAmbienteEmAcabamento } from "../../utils/acabamento";
import { parseNumero } from "../../utils/calculos";
import { getAmbienteCurrentStatus, getEntregaDeadline } from "../../utils/clientes";
import { hasValue, isYes, normalizeText, toDisplayText } from "../../utils/data";
import { F } from "../../utils/gerencialFields";
import { isMarcenariaBlocked } from "../../utils/marcenaria";
import { startOfDay, toDate } from "../../utils/status";
import { temItensTerceirosPendentes } from "../../utils/terceiros";
import { getTipoAmbienteSeguro } from "../../utils/tipoAmbiente";

export const DIAS_EM_BREVE = 7;
const MS_POR_DIA = 86_400_000;

/** Etapas na ordem do fluxo. `status` é o texto de `getAmbienteCurrentStatus`. */
export const ETAPAS = [
  { id: "liberacao", fase: "escritorio", label: "Liberação", status: "Aguardando liberação" },
  { id: "definicoes", fase: "escritorio", label: "Definições", status: "Aguardando definições" },
  { id: "medicao", fase: "escritorio", label: "Medição", status: "Aguardando Medição" },
  { id: "executivo", fase: "escritorio", label: "Executivo", status: "Aguardando Projeto Executivo" },
  // Revisão antes da aprovação (ordem nova). A linha da ordem antiga chega
  // aqui pelo status, então cai na etapa certa mesmo fora desta ordem.
  { id: "revisao", fase: "escritorio", label: "Revisão", status: "Aguardando Revisão" },
  { id: "aprovacao", fase: "escritorio", label: "Aprovação", status: "Aguardando Aprovação" },
  { id: "otimizacao", fase: "escritorio", label: "Otimização do lote", status: "Aguardando Otimização de Lote" },
  { id: "compras", fase: "maquinas", label: "Compras", status: "Aguardando Compras" },
  { id: "corte", fase: "maquinas", label: "Corte", status: "Aguardando Corte" },
  { id: "bordo", fase: "maquinas", label: "Bordo", status: "Aguardando Bordo" },
  { id: "usinagem", fase: "maquinas", label: "Furação e usinagem", status: "Aguardando Usinagem" },
  { id: "marcenaria_inicio", fase: "marcenaria", label: "Início da marcenaria", status: "Aguardando Início de Marcenaria" },
  { id: "marcenaria_fim", fase: "marcenaria", label: "Marcenaria em andamento", status: "Aguardando Finalizar Marcenaria" },
  { id: "terceiros", fase: "acabamento", label: "Terceiros", status: "Aguardando Fornecedores Terceiros" },
  { id: "pintura", fase: "acabamento", label: "Pintura e acabamento", status: "Aguardando Acabamentos" },
  { id: "entrega", fase: "expedicao", label: "Expedição e entrega", status: "Aguardando Entrega" },
];

/** As 5 fases do fluxo. `nome` é o rótulo de uma palavra da legenda da lista. */
export const FASES = [
  { id: "escritorio", label: "Escritório", nome: "Escritório" },
  { id: "maquinas", label: "Compras e máquinas", nome: "Máquinas" },
  { id: "marcenaria", label: "Marcenaria", nome: "Marcenaria" },
  { id: "acabamento", label: "Terceiros e acabamento", nome: "Acabamento" },
  { id: "expedicao", label: "Expedição", nome: "Expedição" },
];

const ETAPA_POR_STATUS = new Map(ETAPAS.map((etapa) => [etapa.status, etapa]));
export const ETAPA_POR_ID = new Map(ETAPAS.map((etapa) => [etapa.id, etapa]));
const ORDEM_DA_ETAPA = new Map(ETAPAS.map((etapa, i) => [etapa.id, i]));

export function etapasDaFase(faseId) {
  return ETAPAS.filter((etapa) => etapa.fase === faseId);
}

export const SITUACOES = [
  { id: "atrasado", label: "Atrasados", tom: "danger" },
  { id: "em_breve", label: `Vencem em ${DIAS_EM_BREVE} dias`, tom: "warning" },
  { id: "no_prazo", label: "No prazo", tom: "success" },
  { id: "sem_prazo", label: "Sem prazo", tom: "neutral" },
  { id: "bloqueado", label: "Bloqueados", tom: "danger" },
];

function texto(valor) {
  return hasValue(valor) ? String(valor).replace(/\s+/g, " ").trim() : "";
}

function diasEntre(inicio, fim) {
  return Math.round((startOfDay(fim).getTime() - startOfDay(inicio).getTime()) / MS_POR_DIA);
}

/** Ambiente em aberto: tem cliente e ainda não foi carregado. */
export function estaEmAberto(row) {
  return hasValue(row?.[F.NOME_DO_CLIENTE]) && !hasValue(row?.[F.CARREGAMENTO]);
}

/**
 * A linha da planilha vista pelo relatório. `hoje` entra por parâmetro para
 * os testes não dependerem do relógio.
 */
export function montarAmbiente(row, indice, hoje = new Date()) {
  const status = getAmbienteCurrentStatus(row, { referenceDate: hoje });
  const etapa = ETAPA_POR_STATUS.get(status.status) || ETAPAS[ETAPAS.length - 1];
  const prazo = toDate(status.deadline);
  const entregaEfetiva = toDate(getEntregaDeadline(row));
  // Etapa sem limite próprio na planilha herda a data de entrega: a tela
  // precisa dizer isso, senão "limite 09/10" pareceria prazo interno.
  const prazoOrigem =
    prazo && entregaEfetiva && prazo.getTime() === entregaEfetiva.getTime() ? "entrega" : "etapa";
  const diasParaPrazo = prazo ? diasEntre(hoje, prazo) : null;

  let situacao = "sem_prazo";
  if (diasParaPrazo !== null) {
    if (diasParaPrazo < 0) situacao = "atrasado";
    else if (diasParaPrazo <= DIAS_EM_BREVE) situacao = "em_breve";
    else situacao = "no_prazo";
  }

  const bloqueado = isMarcenariaBlocked(row);
  const inicioBloqueio = bloqueado ? toDate(row[F.INICIO_DO_BLOQUEIO]) : null;
  const cliente = texto(row[F.NOME_DO_CLIENTE]);
  const pedido = texto(row[F.NUMERO_DO_PEDIDO]);
  const marceneiro = texto(row[F.MARCENEIRO_RESPONSAVEL]);

  return {
    id: `amb-${indice}`,
    indice,
    row,
    chave: chaveAmbiente(row),
    cliente,
    clienteChave: normalizeText(cliente),
    pedido,
    pedidoChave: normalizeText(pedido),
    ambiente: toDisplayText(row[F.AMBIENTE], "Ambiente não informado"),
    lote: texto(row[F.NUMERO_DO_LOTE]),
    tipo: getTipoAmbienteSeguro(row),
    vendedor: texto(row[F.VENDEDOR]),
    consultor: texto(row[F.CONSULTOR]),
    marceneiro: /^sem marceneiro$/i.test(marceneiro) ? "" : marceneiro,
    etapaId: etapa.id,
    etapaLabel: etapa.label,
    etapaOrdem: ORDEM_DA_ETAPA.get(etapa.id),
    faseId: etapa.fase,
    statusTexto: status.status,
    prazo,
    prazoOrigem,
    diasParaPrazo,
    diasAtraso: situacao === "atrasado" ? -diasParaPrazo : 0,
    situacao,
    bloqueado,
    diasBloqueio: inicioBloqueio ? Math.max(0, diasEntre(inicioBloqueio, hoje)) : 0,
    // Selo só quando a pendência é PARALELA; se for a etapa da vez, o status já diz.
    terceirosPendente: etapa.id !== "terceiros" && temItensTerceirosPendentes(row),
    pinturaPendente: etapa.id !== "pintura" && isAmbienteEmAcabamento(row),
    obraFutura: isYes(row[F.OBRA_FUTURA]),
    venda: toDate(row[F.DATA_DA_VENDA]),
    entregaContrato: toDate(row[F.ENTREGA_PREVISTA]),
    carregamentoPrevisto: toDate(row[F.DATA_REPROGRAMADA]),
    valor: parseNumero(row[F.VALOR_DO_AMBIENTE]) || 0,
  };
}

/** Todos os ambientes em aberto da planilha (inclusive obra futura — o filtro decide). */
export function ambientesEmAberto(dados, hoje = new Date()) {
  if (!Array.isArray(dados)) return [];
  const lista = [];
  dados.forEach((row, indice) => {
    if (estaEmAberto(row)) lista.push(montarAmbiente(row, indice, hoje));
  });
  return lista;
}

/** O ambiente pertence à etapa quando ela é a principal OU uma pendência paralela dele. */
export function ambienteNaEtapa(ambiente, etapaId) {
  if (ambiente.etapaId === etapaId) return true;
  if (etapaId === "terceiros") return ambiente.terceirosPendente;
  if (etapaId === "pintura") return ambiente.pinturaPendente;
  return false;
}

export function ambienteNaSituacao(ambiente, situacaoId) {
  return situacaoId === "bloqueado" ? ambiente.bloqueado : ambiente.situacao === situacaoId;
}

// ---------------------------------------------------------------------------
// Resumo
// ---------------------------------------------------------------------------

function menorData(datas) {
  return datas.reduce((menor, d) => (d && (!menor || d < menor) ? d : menor), null);
}

function maiorData(datas) {
  return datas.reduce((maior, d) => (d && (!maior || d > maior) ? d : maior), null);
}

function faixa(datas) {
  return { de: menorData(datas), ate: maiorData(datas), semData: datas.filter((d) => !d).length };
}

/** Números do topo e de cada cartão — a MESMA função, para nunca divergirem. */
export function resumir(ambientes) {
  const porFase = Object.fromEntries(FASES.map((fase) => [fase.id, { total: 0, atrasados: 0 }]));
  const resumo = {
    total: ambientes.length,
    atrasados: 0,
    emBreve: 0,
    noPrazo: 0,
    semPrazo: 0,
    bloqueados: 0,
    maiorAtraso: 0,
    proximoPrazo: null,
    valor: 0,
    porFase,
  };
  ambientes.forEach((ambiente) => {
    if (ambiente.situacao === "atrasado") resumo.atrasados += 1;
    else if (ambiente.situacao === "em_breve") resumo.emBreve += 1;
    else if (ambiente.situacao === "no_prazo") resumo.noPrazo += 1;
    else resumo.semPrazo += 1;
    if (ambiente.bloqueado) resumo.bloqueados += 1;
    if (ambiente.diasAtraso > resumo.maiorAtraso) resumo.maiorAtraso = ambiente.diasAtraso;
    if (ambiente.prazo && ambiente.situacao !== "atrasado") {
      if (!resumo.proximoPrazo || ambiente.prazo < resumo.proximoPrazo) resumo.proximoPrazo = ambiente.prazo;
    }
    resumo.valor += ambiente.valor;
    porFase[ambiente.faseId].total += 1;
    if (ambiente.situacao === "atrasado") porFase[ambiente.faseId].atrasados += 1;
  });
  return resumo;
}

// ---------------------------------------------------------------------------
// Agrupamento
// ---------------------------------------------------------------------------

export const AGRUPAMENTOS = [
  { id: "cliente", label: "Cliente" },
  { id: "pedido", label: "Pedido" },
];

function ordenarAmbientes(ambientes) {
  return [...ambientes].sort(
    (a, b) =>
      b.diasAtraso - a.diasAtraso ||
      a.etapaOrdem - b.etapaOrdem ||
      a.ambiente.localeCompare(b.ambiente, "pt-BR", { numeric: true })
  );
}

function montarGrupo(chave, base, ambientes) {
  const pedidos = new Set(ambientes.map((a) => a.pedidoChave).filter(Boolean));
  return {
    chave,
    ...base,
    ambientes: ordenarAmbientes(ambientes),
    pedidos: pedidos.size,
    resumo: resumir(ambientes),
    venda: faixa(ambientes.map((a) => a.venda)),
    entregaContrato: faixa(ambientes.map((a) => a.entregaContrato)),
    carregamentoPrevisto: faixa(ambientes.map((a) => a.carregamentoPrevisto)),
  };
}

/**
 * Agrupa por cliente ou por pedido. Por pedido a chave é PEDIDO + CLIENTE:
 * o mesmo número em dois clientes (hoje 1, 1105 e 1275) vira dois cartões,
 * sinalizados (`pedidoAmbiguo`) — nada é somado entre clientes. Linhas sem
 * número de pedido ficam num cartão "Sem pedido" do próprio cliente.
 *
 * `pedidosAmbiguos`: Set de pedidos normalizados com mais de um cliente
 * (`indiceDeIdentidade(dados).pedidosAmbiguos`).
 */
export function agrupar(ambientes, agrupamento, pedidosAmbiguos = new Set()) {
  const baldes = new Map();
  ambientes.forEach((ambiente) => {
    const chave =
      agrupamento === "pedido" ? `${ambiente.pedidoChave || "-"}|${ambiente.clienteChave}` : ambiente.clienteChave;
    const balde = baldes.get(chave) || [];
    balde.push(ambiente);
    baldes.set(chave, balde);
  });

  return [...baldes.entries()].map(([chave, lista]) => {
    const primeiro = lista[0];
    if (agrupamento === "pedido") {
      return montarGrupo(
        chave,
        {
          tipo: "pedido",
          titulo: primeiro.pedido ? `Pedido ${primeiro.pedido}` : "Sem pedido",
          subtitulo: primeiro.cliente,
          cliente: primeiro.cliente,
          pedido: primeiro.pedido,
          semPedido: !primeiro.pedido,
          pedidoAmbiguo: pedidosAmbiguos.has(primeiro.pedidoChave),
        },
        lista
      );
    }
    const numeros = [...new Set(lista.map((a) => a.pedido).filter(Boolean))].sort((a, b) =>
      a.localeCompare(b, "pt-BR", { numeric: true })
    );
    return montarGrupo(
      chave,
      {
        tipo: "cliente",
        titulo: primeiro.cliente,
        subtitulo: numeros.length ? `Pedido${numeros.length > 1 ? "s" : ""} ${numeros.join(", ")}` : "Sem pedido",
        cliente: primeiro.cliente,
        pedido: numeros.join(", "),
        semPedido: numeros.length === 0,
        pedidoAmbiguo: lista.some((a) => pedidosAmbiguos.has(a.pedidoChave)),
      },
      lista
    );
  });
}

// ---------------------------------------------------------------------------
// Ordenação dos grupos
// ---------------------------------------------------------------------------

export const ORDENS = [
  { id: "critico", label: "Mais crítico" },
  { id: "entrega", label: "Próxima entrega" },
  { id: "venda", label: "Venda mais antiga" },
  { id: "nome", label: "Nome" },
];

function porNome(a, b) {
  return (
    a.cliente.localeCompare(b.cliente, "pt-BR") ||
    String(a.pedido).localeCompare(String(b.pedido), "pt-BR", { numeric: true })
  );
}

function tempo(data) {
  return data ? data.getTime() : Number.POSITIVE_INFINITY;
}

function entregaMaisProxima(grupo) {
  return menorData(grupo.ambientes.map((a) => a.carregamentoPrevisto || a.entregaContrato));
}

const COMPARADORES = {
  // Maior atraso no topo (como o relatório impresso); depois quem vence antes.
  critico: (a, b) =>
    b.resumo.maiorAtraso - a.resumo.maiorAtraso ||
    b.resumo.atrasados - a.resumo.atrasados ||
    tempo(a.resumo.proximoPrazo) - tempo(b.resumo.proximoPrazo) ||
    porNome(a, b),
  entrega: (a, b) => tempo(entregaMaisProxima(a)) - tempo(entregaMaisProxima(b)) || porNome(a, b),
  venda: (a, b) => tempo(a.venda.de) - tempo(b.venda.de) || porNome(a, b),
  nome: porNome,
};

export function ordenarGrupos(grupos, ordem = "critico") {
  return [...grupos].sort(COMPARADORES[ordem] || COMPARADORES.critico);
}
